import { useCallback, useState } from "react";
import AdminStatePanel from "../../../layout/AdminStatePanel";
import { useAdminWeatherRadarHealth } from "../../../../../../hooks/useAdminWeatherRadarHealth";
import {
  exportRadarDiagnostics,
  exportSiataDiagnostics,
  fetchBartLatestFrame,
  fetchRadarDiagnostics,
  fetchSiataDiagnostics,
  fetchSiataLatestFrame,
  runBartCacheCleanup,
  runBartCacheSync,
  runBartDiscovery,
  runSiataCacheCleanup,
  runSiataCacheSync,
  runSiataDiscovery,
  runAerocivilDiscovery,
  runAerocivilCacheSync,
  fetchAerocivilLatestFrame,
} from "../../../../../../weather/adminRadarActionsApi";
import WeatherRadarActionResultPanel from "./WeatherRadarActionResultPanel";
import WeatherRadarBartDiagnosticsPanel from "./WeatherRadarBartDiagnosticsPanel";
import WeatherRadarFallbackPanel from "./WeatherRadarFallbackPanel";
import WeatherValleyPreferredRolloutPanel from "./WeatherValleyPreferredRolloutPanel.jsx";
import WeatherAerocivilProductionRolloutPanel from "./WeatherAerocivilProductionRolloutPanel.jsx";
import WeatherAerocivilCalibrationPanel from "./WeatherAerocivilCalibrationPanel.jsx";
import WeatherRadarHealthActions from "./WeatherRadarHealthActions";
import WeatherRadarHealthSummaryCards from "./WeatherRadarHealthSummaryCards";
import WeatherRadarHealthTable from "./WeatherRadarHealthTable";
import WeatherRadarLatestFrameModal from "./WeatherRadarLatestFrameModal";
import { buildSiataSantaElenaContactPayload } from "../../../../../../constants/siataSantaElenaRadarPlan.js";
import { buildAerocivilAviationContactPayload } from "../../../../../../constants/aerocivilAviationRadarPlan.js";

const PIPELINE_ACTIONS = new Set([
  "bart_discovery",
  "bart_cache_sync",
  "bart_cache_cleanup",
  "siata_radar_discover",
  "siata_radar_sync",
  "siata_radar_cleanup",
  "aerocivil_radar_discover",
  "aerocivil_radar_sync",
]);

const WeatherRadarHealthSection = ({ canManage = false, onToast }) => {
  const { health, loading, refreshing, errorMessage, lastRefreshedAt, refreshHealth } =
    useAdminWeatherRadarHealth();

  const [runningAction, setRunningAction] = useState(null);
  const [lastActionResult, setLastActionResult] = useState(null);
  const [frameModalOpen, setFrameModalOpen] = useState(false);
  const [framePayload, setFramePayload] = useState(null);
  const [frameLoading, setFrameLoading] = useState(false);
  const [frameError, setFrameError] = useState("");

  const apiBase = process.env.REACT_APP_API_BASE_URL || "";

  const notify = useCallback(
    (tone, message) => {
      if (onToast) {
        onToast({ tone, message });
      }
    },
    [onToast]
  );

  const runAction = useCallback(
    async (actionKey, runner) => {
      setRunningAction(actionKey);
      try {
        const result = await runner();
        setLastActionResult(result);
        await refreshHealth({ refresh: true, silent: true });
        notify("success", result.message || "Action completed.");
        return result;
      } catch (error) {
        const failure = {
          ok: false,
          action: actionKey,
          status: "error",
          message: error.message || "Action failed.",
          finishedAt: new Date().toISOString(),
          errors: [{ code: "ACTION_FAILED", message: error.message || "Action failed." }],
        };
        setLastActionResult(failure);
        notify("error", failure.message);
        return failure;
      } finally {
        setRunningAction(null);
      }
    },
    [notify, refreshHealth]
  );

  const handleRunDiscovery = useCallback(() => {
    return runAction("bart_discovery", runBartDiscovery);
  }, [runAction]);

  const handleSyncCache = useCallback(() => {
    return runAction("bart_cache_sync", runBartCacheSync);
  }, [runAction]);

  const handleCleanupStale = useCallback(() => {
    const confirmed = window.confirm(
      "This will remove old cached radar frames but keep recent renderable frames. Continue?"
    );

    if (!confirmed) {
      return null;
    }

    return runAction("bart_cache_cleanup", runBartCacheCleanup);
  }, [runAction]);

  const handleSiataDiscovery = useCallback(() => {
    return runAction("siata_radar_discover", runSiataDiscovery);
  }, [runAction]);

  const handleSiataSync = useCallback(() => {
    return runAction("siata_radar_sync", runSiataCacheSync);
  }, [runAction]);

  const handleSiataCleanup = useCallback(() => {
    const confirmed = window.confirm(
      "This will remove stale SIATA cached frames but keep recent renderable frames. Continue?"
    );

    if (!confirmed) {
      return null;
    }

    return runAction("siata_radar_cleanup", runSiataCacheCleanup);
  }, [runAction]);

  const handleAerocivilDiscovery = useCallback(() => {
    return runAction("aerocivil_radar_discover", runAerocivilDiscovery);
  }, [runAction]);

  const handleAerocivilSync = useCallback(() => {
    return runAction("aerocivil_radar_sync", runAerocivilCacheSync);
  }, [runAction]);

  const handleOpenAerocivilLatestFrame = useCallback(async () => {
    setFrameModalOpen(true);
    setFrameLoading(true);
    setFrameError("");
    setFramePayload(null);

    try {
      const payload = await fetchAerocivilLatestFrame();
      setFramePayload(payload);
    } catch (error) {
      setFrameError(error.message || "Unable to load latest cached Aerocivil frame.");
    } finally {
      setFrameLoading(false);
    }
  }, []);

  const handleOpenSiataLatestFrame = useCallback(async () => {
    setFrameModalOpen(true);
    setFrameLoading(true);
    setFrameError("");
    setFramePayload(null);

    try {
      const payload = await fetchSiataLatestFrame();
      setFramePayload(payload);
    } catch (error) {
      setFrameError(error.message || "Unable to load latest cached SIATA frame.");
    } finally {
      setFrameLoading(false);
    }
  }, []);

  const handleCopyDiagnostics = useCallback(async () => {
    try {
      const payload = await fetchRadarDiagnostics({ refresh: true });
      const text = JSON.stringify(payload, null, 2);
      await navigator.clipboard.writeText(text);
      notify("success", "Radar diagnostics copied.");
    } catch (error) {
      notify("error", error.message || "Unable to copy radar diagnostics.");
    }
  }, [notify]);

  const handleExportDiagnostics = useCallback(async () => {
    try {
      const result = await exportRadarDiagnostics({ refresh: true });
      setLastActionResult({
        ok: true,
        action: "radar_diagnostics_export",
        status: "completed",
        message: `Diagnostics exported as ${result.filename}.`,
        finishedAt: new Date().toISOString(),
      });
      notify("success", "Radar diagnostics exported.");
    } catch (error) {
      notify("error", error.message || "Unable to export radar diagnostics.");
    }
  }, [notify]);

  const handleOpenLatestFrame = useCallback(async () => {
    setFrameModalOpen(true);
    setFrameLoading(true);
    setFrameError("");
    setFramePayload(null);

    try {
      const payload = await fetchBartLatestFrame();
      setFramePayload(payload);
    } catch (error) {
      setFrameError(error.message || "Unable to load latest cached frame.");
    } finally {
      setFrameLoading(false);
    }
  }, []);

  const handleCopySourceDiagnostics = useCallback(
    async (sourceKey) => {
      try {
        const payload = await fetchRadarDiagnostics({ refresh: false });
        const text = JSON.stringify(
          { [sourceKey]: payload.combinedHealth?.sources?.[sourceKey] || payload[sourceKey] },
          null,
          2
        );
        await navigator.clipboard.writeText(text);
        notify("success", "Source diagnostics copied.");
      } catch (error) {
        notify("error", error.message || "Unable to copy source diagnostics.");
      }
    },
    [notify]
  );

  const handleOpenFrame = useCallback(
    (url) => {
      const target = url?.startsWith("http") ? url : `${apiBase}${url}`;
      if (target) {
        window.open(target, "_blank", "noopener,noreferrer");
      }
    },
    [apiBase]
  );

  const handleViewPlan = useCallback(
    (row) => {
      if (row?.id === "aerocivil_aviation_radar") {
        const element = document.querySelector("#aerocivil-aviation-radar-plan");
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
          notify("success", "Scrolled to Aerocivil radar integration plan.");
          return;
        }
        const payload = buildAerocivilAviationContactPayload();
        navigator.clipboard
          .writeText(payload.inventory.planDoc)
          .then(() => notify("success", "Plan document path copied."))
          .catch((error) => notify("error", error.message || "Unable to copy plan path."));
        return;
      }

      const anchor = row?.planAnchor || "#siata-santa-elena-radar-plan";
      const element = document.querySelector(anchor);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
        notify("success", "Scrolled to SIATA radar integration plan.");
        return;
      }

      const payload = buildSiataSantaElenaContactPayload();
      navigator.clipboard
        .writeText(payload.inventory.planDoc)
        .then(() => notify("success", "Plan document path copied."))
        .catch((error) => notify("error", error.message || "Unable to copy plan path."));
    },
    [notify]
  );

  const handleCopyContact = useCallback(async () => {
    try {
      const payload = buildSiataSantaElenaContactPayload();
      const text = [
        payload.subject,
        "",
        "--- English ---",
        payload.english,
        "",
        "--- Español ---",
        payload.spanish,
      ].join("\n");
      await navigator.clipboard.writeText(text);
      notify("success", "SIATA contact request draft copied.");
    } catch (error) {
      notify("error", error.message || "Unable to copy contact request.");
    }
  }, [notify]);

  const handleViewApprovalDoc = useCallback(
    async (row) => {
      const path =
        row?.approvalDocPath ||
        (row?.id === "aerocivil_aviation_radar"
          ? buildAerocivilAviationContactPayload().inventory.approvalDocPath
          : buildSiataSantaElenaContactPayload().inventory.approvalDocPath) ||
        "docs/weather/siata-santa-elena-radar-approval.md";

      try {
        await navigator.clipboard.writeText(path);
        notify("success", "SIATA approval doc path copied.");
      } catch (error) {
        notify("error", error.message || "Unable to copy approval doc path.");
      }
    },
    [notify]
  );

  const frameImageUrl = framePayload?.frame?.publicUrl
    ? `${apiBase}${framePayload.frame.publicUrl}`
    : null;

  const pipelineBusy = PIPELINE_ACTIONS.has(runningAction);

  return (
    <section id="radar-health" className="weather-radar-health-section">
      <div className="weather-radar-health-section__header">
        <div>
          <h2 className="admin-section-title">Weather Source Health — Radar</h2>
          <p className="weather-radar-health-section__description">
            Combined IDEAM BART, SIATA Santa Elena, RainViewer fallback, cache freshness, and
            Valley Preferred source-mode diagnostics.
          </p>
        </div>
      </div>

      <WeatherRadarHealthActions
        canManage={canManage}
        lastRefreshedAt={lastRefreshedAt}
        refreshing={refreshing || loading}
        runningAction={runningAction}
        pipelineBusy={pipelineBusy}
        onRefresh={() => refreshHealth({ refresh: true })}
        onCopyDiagnostics={handleCopyDiagnostics}
        onExportDiagnostics={handleExportDiagnostics}
        onOpenLatestFrame={handleOpenLatestFrame}
        onOpenSiataLatestFrame={handleOpenSiataLatestFrame}
        onRunDiscovery={handleRunDiscovery}
        onSyncCache={handleSyncCache}
        onCleanupStale={handleCleanupStale}
        onSiataDiscovery={handleSiataDiscovery}
        onSiataSync={handleSiataSync}
        onSiataCleanup={handleSiataCleanup}
        onAerocivilDiscovery={handleAerocivilDiscovery}
        onAerocivilSync={handleAerocivilSync}
        onOpenAerocivilLatestFrame={handleOpenAerocivilLatestFrame}
        siataEnabled={Boolean(health?.sources?.siataSantaElena)}
        aerocivilEnabled={Boolean(health?.sources?.aerocivilAviation)}
      />

      <WeatherRadarActionResultPanel result={lastActionResult} />

      {loading && !health ? (
        <AdminStatePanel variant="loading" message="Fetching combined radar health…" />
      ) : errorMessage ? (
        <AdminStatePanel
          variant="error"
          message={`Weather health endpoint unavailable. ${errorMessage}`}
          onRetry={() => refreshHealth({ refresh: true })}
        />
      ) : (
        <>
          {health?.message ? (
            <p className="weather-radar-health-notice" role="status">
              {health.message}
            </p>
          ) : null}

          {health?.fallbackActive ? (
            <p
              className="weather-radar-health-notice weather-radar-health-notice--fallback"
              role="status"
            >
              RainViewer backup is currently rendering because official radar is unavailable.
            </p>
          ) : null}

          <WeatherRadarHealthSummaryCards health={health} />

          <div className="admin-dash__card admin-dash__card--wide panel-card">
            <h3 className="admin-section-title">Radar source health</h3>
            <WeatherRadarHealthTable
              health={health}
              canManage={canManage}
              onOpenFrame={handleOpenFrame}
              onCopyDiagnostics={handleCopySourceDiagnostics}
              onViewPlan={handleViewPlan}
              onCopyContact={handleCopyContact}
              onViewApprovalDoc={handleViewApprovalDoc}
              onSiataDiscover={handleSiataDiscovery}
              onSiataSync={handleSiataSync}
              onSiataCleanup={handleSiataCleanup}
              onAerocivilDiscover={handleAerocivilDiscovery}
              onAerocivilSync={handleAerocivilSync}
            />

            <div
              id="aerocivil-aviation-radar-plan"
              className="weather-radar-health-plan-note admin-dash__card panel-card"
            >
              <h4 className="admin-section-title">Aerocivil Aviation Radar — operations</h4>
              <p>
                Official (aeronautical) radar from Aerocivil LEADS WxWeb. Independent toggle — does
                not alter Valley Preferred. Admin discovery, sync, and health when{" "}
                <code>AEROCIVIL_RADAR_ENABLED=true</code>.
              </p>
              <p>
                Approval contract: <code>docs/weather/aerocivil-radar-approval.md</code>
              </p>
              <p>
                Discovery plan: <code>docs/weather/aerocivil-radar-source-discovery.md</code>
              </p>
            </div>

            <div
              id="siata-santa-elena-radar-plan"
              className="weather-radar-health-plan-note admin-dash__card panel-card"
            >
              <h4 className="admin-section-title">SIATA Santa Elena Radar — operations</h4>
              <p>
                Local official radar for Medellín / Aburrá Valley. Admin health checks, discovery, sync,
                cleanup, and diagnostics are available when the server feature flag is enabled. Valley
                Preferred source mode uses SIATA when healthy and inside local coverage.
              </p>
              <p>
                Approval contract:{" "}
                <code>docs/weather/siata-santa-elena-radar-approval.md</code>
              </p>
              <p>
                Access plan: <code>docs/weather/siata-santa-elena-radar-plan.md</code>
              </p>
            </div>
          </div>

          <div className="weather-radar-health-panels">
            <WeatherRadarBartDiagnosticsPanel
              bart={health?.sources?.ideamBart}
              showDebugFields={canManage}
            />
            <WeatherRadarFallbackPanel health={health} />
            <WeatherAerocivilProductionRolloutPanel onToast={notify} />
            <WeatherAerocivilCalibrationPanel canManage={canManage} onToast={notify} />

            <WeatherValleyPreferredRolloutPanel onToast={notify} />
          </div>
        </>
      )}

      <WeatherRadarLatestFrameModal
        framePayload={framePayload}
        imageUrl={frameImageUrl}
        loading={frameLoading}
        errorMessage={frameError}
        onClose={() => {
          setFrameModalOpen(false);
          setFramePayload(null);
          setFrameError("");
        }}
        isOpen={frameModalOpen}
      />
    </section>
  );
};

export default WeatherRadarHealthSection;
